import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMasterData, useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface MedicineFormData {
  pond_id: string;
  medicine_date: string;
  medicine_type: string;
  quantity: number;
  unit: string;
  remarks?: string;
  location_id?: string;
}

interface MedicineLog {
  id: string;
  pond_id: string;
  medicine_date: string;
  medicine_type: string;
  quantity: number;
  unit: string;
  remarks?: string;
  created_at: string;
  created_by: string;
}

interface PondMedicineData {
  id: string;
  name: string;
  pond_type: string;
  medicine_type: string;
  quantity: string;
  unit: string;
  remarks: string;
  expanded: boolean;
}

const MedicineForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [medicineDate, setMedicineDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [pondsData, setPondsData] = useState<PondMedicineData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { data: medicineTypes } = useMasterData('medicine_types', selectedLocation?.id || null);
  const [inventoryBalances, setInventoryBalances] = useState<any[]>([]);
  // Load inventory balances for selected location
  useEffect(() => {
    const loadBalances = async () => {
      if (!selectedLocation) {
        setInventoryBalances([]);
        return;
      }
      try {
        const res = await apiClient.get('/inventory/balances', {
          params: { location_id: selectedLocation.id },
        });
        setInventoryBalances(res.data || []);
      } catch {
        setInventoryBalances([]);
      }
    };
    loadBalances();
  }, [selectedLocation]);

  // Build a quick lookup for inventory on hand by medicine name
  const medicineStockByName: Record<string, number> = React.useMemo(() => {
    const stock: Record<string, number> = {};
    (inventoryBalances || []).forEach((b: any) => {
      if (b?.item_name) stock[b.item_name] = b.on_hand_quantity || 0;
    });
    return stock;
  }, [inventoryBalances]);

  // Show all medicine types from master data; annotate with stock info
  const availableMedicineTypes = (medicineTypes || []) as any[];

  const { mutate: createMedicineLog, loading: creating } = useMutation<MedicineLog, MedicineFormData>();

  useEffect(() => {
    if (ponds) {
      setPondsData(ponds.map(pond => ({
        id: pond.id,
        name: pond.name,
        pond_type: pond.pond_type,
        medicine_type: '',
        quantity: '',
        unit: '',
        remarks: '',
        expanded: false
      })));
    }
  }, [ponds]);

  const updatePond = (pondId: string, field: keyof PondMedicineData, value: string | boolean) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, [field]: value } : pond
    ));
  };

  const togglePondExpansion = (pondId: string) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, expanded: !pond.expanded } : pond
    ));
  };

  const handleVoiceInput = (pondId: string) => {
    alert(`Voice input for ${pondId} - Feature would integrate with device microphone`);
  };

  const clearAllPonds = () => {
    setPondsData(pondsData.map(pond => ({ 
      ...pond, 
      medicine_type: '', 
      quantity: '', 
      unit: '',
      remarks: '' 
    })));
  };

  // Auto-select unit based on selected medicine type
  useEffect(() => {
    pondsData.forEach(pond => {
      if (pond.medicine_type && medicineTypes) {
        const medicine = medicineTypes.find(m => m.name === pond.medicine_type);
        if (medicine && medicine.unit) {
          updatePond(pond.id, 'unit', medicine.unit);
        }
      }
    });
  }, [pondsData.map(p => p.medicine_type).join(','), medicineTypes]);

  const handleSubmit = async () => {
    const validEntries = pondsData.filter(pond => pond.medicine_type && pond.quantity && pond.unit);
    
    if (validEntries.length === 0) {
      alert('Please fill medicine data for at least one pond');
      return;
    }

    try {
      setLoading(true);
      const promises = validEntries.map(pond => 
        createMedicineLog('/medicine-logs', {
          pond_id: pond.id,
          medicine_date: medicineDate,
          medicine_type: pond.medicine_type,
          quantity: parseFloat(pond.quantity),
          unit: pond.unit,
          remarks: pond.remarks || undefined,
          location_id: selectedLocation?.id,
        })
      );
      
      await Promise.all(promises);
      alert(`✅ Successfully recorded medicine data for ${validEntries.length} ponds on ${medicineDate}`);
      
      // Reset form
      setPondsData(pondsData.map(pond => ({ 
        ...pond, 
        medicine_type: '', 
        quantity: '', 
        unit: '',
        remarks: '' 
      })));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTotalQuantity = () => {
    return pondsData.reduce((sum, pond) => sum + (parseFloat(pond.quantity) || 0), 0).toFixed(2);
  };

  const getFilledPondsCount = () => {
    return pondsData.filter(pond => pond.medicine_type && pond.quantity && pond.unit).length;
  };

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-800">💊 Medicine Log - EOD</h1>
        </div>
        
        {/* Date Selection */}
        <div className="flex items-center gap-2 mb-3">
          <input
            type="date"
            value={medicineDate}
            onChange={(e) => setMedicineDate(e.target.value)}
            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={clearAllPonds}
            className="bg-red-100 text-red-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
          >
            <span className="text-xs">🔄</span>
            Clear All
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="bg-red-50 p-2 rounded-lg">
          <div className="flex justify-between text-xs text-red-700 mb-1">
            <span>Progress</span>
            <span>{getFilledPondsCount()}/{pondsData.length} ponds completed</span>
          </div>
          <div className="w-full bg-red-200 rounded-full h-2">
            <div 
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(getFilledPondsCount() / pondsData.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Ponds List */}
      <div className="p-4 space-y-3">
        {pondsData.map((pond) => (
          <div key={pond.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Pond Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{pond.name}</h3>
                  <p className="text-sm text-gray-500">{pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}</p>
                </div>
                <button
                  onClick={() => togglePondExpansion(pond.id)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  {pond.expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Entry Fields */}
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Medicine Type *
                </label>
                <select
                  value={pond.medicine_type}
                  onChange={(e) => updatePond(pond.id, 'medicine_type', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-center text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Select Medicine</option>
                  {availableMedicineTypes?.map((medicine: any) => {
                    const stock = medicineStockByName[medicine.name] ?? 0;
                    const unitPart = medicine.unit ? `/${medicine.unit}` : '';
                    const label = `${medicine.name}${unitPart} (${stock} in stock)`;
                    return (
                      <option key={medicine.id} value={medicine.name}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={pond.quantity}
                    onChange={(e) => updatePond(pond.id, 'quantity', e.target.value)}
                    placeholder="0.00"
                    className="w-full p-3 border border-gray-300 rounded-lg text-center text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Unit * (from Medicine Type)
                  </label>
                  <input
                    value={pond.unit}
                    readOnly
                    placeholder="Auto from medicine type"
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-center text-lg"
                  />
                </div>
              </div>

              {/* Expanded Fields */}
              {pond.expanded && (
                <div className="space-y-3 border-t border-gray-100 pt-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-700">
                        Remarks (Optional)
                      </label>
                      <button
                        onClick={() => handleVoiceInput(pond.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <span className="text-sm">🎤</span>
                      </button>
                    </div>
                    <textarea
                      value={pond.remarks}
                      onChange={(e) => updatePond(pond.id, 'remarks', e.target.value)}
                      placeholder="Treatment purpose, dosage instructions, etc."
                      rows={2}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary & Submit */}
      <div className="p-4 bg-white border-t border-gray-200 sticky bottom-0">
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div className="bg-red-50 p-3 rounded-lg text-center">
            <div className="text-xs text-red-600 font-medium">Total Medicine Quantity</div>
            <div className="text-lg font-bold text-red-700">{getTotalQuantity()} units</div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || creating}
          className="w-full bg-red-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading || creating ? <LoadingSpinner size="sm" /> : <span className="text-lg">💾</span>}
          Submit EOD Medicine ({getFilledPondsCount()}/{pondsData.length})
        </button>
      </div>
    </div>
  );
};

export default MedicineForm;